import { useEffect, useRef, useState } from 'react';
import {
  CUSTOM_REACTION_RE,
  customEmojiUrl,
  type ActionRow,
  type Button,
  type Select,
} from '@comms/shared';
import { useIsMobile } from '../hooks/mobile';
import { Icon, Spinner, cx } from './ui';

const STYLES: Record<Button['style'], string> = {
  primary: 'bg-accent text-white hover:bg-accent-hover',
  secondary: 'bg-bg-3 text-text-0 hover:bg-bg-hover',
  success: 'bg-success text-white hover:brightness-110',
  danger: 'bg-danger text-white hover:brightness-110',
  link: 'bg-bg-3 text-text-0 hover:bg-bg-hover',
};

function ComponentEmoji({ emoji }: { emoji: string }) {
  const custom = CUSTOM_REACTION_RE.exec(emoji);
  if (custom)
    return <img src={customEmojiUrl(custom[2]!)} alt={`:${custom[1]}:`} className="h-5 w-5" />;
  return <span aria-hidden>{emoji}</span>;
}

/** Buttons and select menus on a bot's message. Using one sends it to the bot. */
export function MessageComponents({
  rows,
  pending,
  onUse,
}: {
  rows: ActionRow[];
  /** the customId waiting for the bot's answer */
  pending?: string;
  /** null when you can't use them here (e.g. a search result) */
  onUse: ((customId: string, label: string, values?: string[]) => void) | null;
}) {
  return (
    <div className="mt-2 space-y-2" data-components>
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap gap-2">
          {row.components.map((c) =>
            c.type === 'select' ? (
              <SelectMenu
                key={c.customId}
                select={c}
                busy={pending === c.customId}
                disabled={c.disabled || !onUse || !!pending}
                onPick={(values, label) => onUse?.(c.customId, label, values)}
              />
            ) : (
              <ButtonView
                key={c.customId ?? c.url}
                b={c}
                busy={!!c.customId && pending === c.customId}
                disabled={c.disabled || (c.style !== 'link' && (!onUse || !!pending))}
                onClick={() => c.customId && onUse?.(c.customId, c.label ?? c.emoji ?? 'a button')}
              />
            ),
          )}
        </div>
      ))}
    </div>
  );
}

function ButtonView({
  b,
  busy,
  disabled,
  onClick,
}: {
  b: Button;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const className = cx(
    'relative flex min-h-11 items-center gap-1.5 rounded px-4 text-sm font-medium transition md:min-h-8',
    STYLES[b.style],
    disabled && 'cursor-not-allowed opacity-50 hover:brightness-100',
  );
  const inner = (
    <>
      {b.emoji && <ComponentEmoji emoji={b.emoji} />}
      {b.label && <span className={cx(busy && 'invisible')}>{b.label}</span>}
      {busy && <Spinner className="absolute inset-0 m-auto h-4 w-4" />}
      {b.style === 'link' && <Icon name="external" className="h-3.5 w-3.5" />}
    </>
  );
  if (b.style === 'link') {
    const host = (() => {
      try {
        return new URL(b.url!).host;
      } catch {
        return '';
      }
    })();
    if (disabled) return <span className={className}>{inner}</span>;
    return (
      <a
        href={b.url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        title={`Opens ${host}`}
        className={className}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      aria-busy={busy}
      onClick={onClick}
      className={className}
    >
      {inner}
    </button>
  );
}

function SelectMenu({
  select,
  busy,
  disabled,
  onPick,
}: {
  select: Select;
  busy: boolean;
  disabled: boolean;
  onPick: (values: string[], label: string) => void;
}) {
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const defaults = select.options.filter((o) => o.default).map((o) => o.value);
  const [picked, setPicked] = useState<string[]>(defaults);
  const ref = useRef<HTMLDivElement>(null);
  const multi = select.maxValues > 1;
  const listId = `select-${select.customId}`;

  useEffect(() => {
    if (!open || mobile) return;
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open, mobile]);

  const submit = (values: string[]) => {
    setOpen(false);
    setPicked(values);
    const labels = select.options.filter((o) => values.includes(o.value)).map((o) => o.label);
    onPick(values, labels.join(', ') || select.placeholder || 'a menu');
  };
  const toggle = (value: string) => {
    if (!multi) return submit([value]);
    setPicked((p) =>
      p.includes(value)
        ? p.filter((v) => v !== value)
        : p.length < select.maxValues
          ? [...p, value]
          : p,
    );
  };
  const summary =
    select.options
      .filter((o) => picked.includes(o.value))
      .map((o) => o.label)
      .join(', ') ||
    select.placeholder ||
    'Make a selection';
  const valid = picked.length >= select.minValues && picked.length <= select.maxValues;

  const list = (
    <div
      id={listId}
      role="listbox"
      aria-multiselectable={multi}
      aria-label={select.placeholder ?? 'Options'}
      onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
      className="max-h-72 overflow-y-auto py-1"
    >
      {select.options.map((o) => {
        const on = picked.includes(o.value);
        return (
          <div
            key={o.value}
            role="option"
            aria-selected={on}
            tabIndex={0}
            onClick={() => toggle(o.value)}
            onKeyDown={(e) =>
              (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggle(o.value))
            }
            className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-bg-hover focus:bg-bg-hover focus:outline-none md:min-h-9"
          >
            {o.emoji && <ComponentEmoji emoji={o.emoji} />}
            <span className="min-w-0 flex-1">
              <span className="block text-text-0">{o.label}</span>
              {o.description && <span className="block text-xs text-text-3">{o.description}</span>}
            </span>
            {multi ? (
              <span
                aria-hidden
                className={cx(
                  'flex h-5 w-5 items-center justify-center rounded border',
                  on ? 'border-accent bg-accent text-white' : 'border-text-3',
                )}
              >
                {on && <Icon name="check" className="h-3.5 w-3.5" />}
              </span>
            ) : (
              on && <Icon name="check" className="h-4 w-4 text-accent" />
            )}
          </div>
        );
      })}
      {multi && (
        <div className="flex items-center justify-between gap-2 border-t border-bg-3 px-3 pt-2">
          <span className="text-xs text-text-3">
            {select.minValues === select.maxValues
              ? `Pick ${select.maxValues}`
              : `Pick ${select.minValues}–${select.maxValues}`}
          </span>
          <button
            type="button"
            disabled={!valid}
            onClick={() => submit(picked)}
            className="min-h-9 rounded bg-accent px-3 text-sm font-medium text-white disabled:opacity-50"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div ref={ref} className="relative w-full max-w-md">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen(!open)}
        className={cx(
          'flex min-h-11 w-full items-center gap-2 rounded border border-bg-0 bg-bg-1 px-3 text-left text-sm md:min-h-10',
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-text-3',
        )}
      >
        <span
          className={cx('min-w-0 flex-1 truncate', picked.length ? 'text-text-0' : 'text-text-3')}
        >
          {summary}
        </span>
        {busy ? (
          <Spinner className="h-4 w-4" />
        ) : (
          <Icon name="chevron" className="h-4 w-4 text-text-2" />
        )}
      </button>
      {open &&
        (mobile ? (
          <div
            className="fixed inset-0 z-50 flex items-end bg-black/50"
            onClick={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-label={select.placeholder ?? 'Choose'}
              className="safe-bottom w-full rounded-t-2xl bg-bg-1 p-2 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-bg-3" />
              {list}
            </div>
          </div>
        ) : (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded border border-bg-0 bg-bg-1 pb-2 shadow-xl">
            {list}
          </div>
        ))}
    </div>
  );
}
